/**
 * Creature spec sheets — the doc 16 pipeline's second lane.
 *
 * A structure and a fish are not the same kind of object and pretending they
 * are would break both. A structure stands on a footprint: its anchor is the
 * ground-plane centre of a diamond, it is built strictly upward, and it owns a
 * foundation pad every other body draws over. A fish hangs in open water: its
 * anchor is the SWIM LINE, the body straddles it, and there is no pad, no
 * contact shadow and no tile it belongs to.
 *
 * So the six laws get a creature reading (doc 16 §0). L4 and L6 are unchanged —
 * integers are integers and an outline is an outline. L5 swaps the structure
 * ramp for `faunaRamp` and the signature light for the depth's own
 * bioluminescence. L1, L2 and L3 become the two checks below:
 *
 *   swimlineAlignment  ink centred on the anchor in x (+-1) and straddling it
 *                      in y — a creature that drifts off its anchor slides off
 *                      its tile on screen, which is the `art-test-03` bug
 *                      wearing fins.
 *   sizeBudget         reach / rise / drop per size class. A creature too wide
 *                      occludes its neighbours and one too tall occludes the
 *                      map, which is Law L3's actual concern.
 */

import type { DepthPalette, FaunaRamp } from '../render/palette.ts';
import type { Color, PixelSurface } from '../render/surface.ts';

/**
 * Doc 16 §1's creature row. `hurt` and `death` are the two the placeholder art
 * never had: a fish used to take fifty damage and a kill used to be a sprite
 * that stopped existing.
 */
export type CreatureState = 'idle' | 'move' | 'attack' | 'hurt' | 'death';

export const CREATURE_STATES: CreatureState[] = ['idle', 'move', 'attack', 'hurt', 'death'];

/** The states that loop, where frame 0 must equal frame N. */
export const CREATURE_LOOPS: CreatureState[] = ['idle', 'move'];

export type CreatureSize = 'small' | 'medium' | 'large' | 'boss';

export interface SizeBox {
  /** Half-width in px, measured from the anchor. */
  reach: number;
  /** Px of ink allowed above the swim line. */
  rise: number;
  /** Px of ink allowed below it. */
  drop: number;
}

/**
 * The occlusion budget, by class. A tile is 32x16 internal px, so `medium`
 * fills roughly one tile and `boss` roughly two — which is the whole point of
 * having a boss class rather than letting every animal grow.
 */
export const SIZE_BOXES: Record<CreatureSize, SizeBox> = {
  small: { reach: 12, rise: 10, drop: 10 },
  medium: { reach: 18, rise: 14, drop: 14 },
  large: { reach: 24, rise: 16, drop: 16 },
  boss: { reach: 32, rise: 20, drop: 22 },
};

/** Doc 06's four properties, as the markers doc 10 fixes the vocabulary for. */
export interface Markers {
  /** [#] plate bands in bone. */
  armored: boolean;
  /** [>] motion streaks trailing the body. */
  swift: boolean;
  /** (~) half-dither ghost — applied only while actually hidden. */
  cloaked: boolean;
  /** (o) motes of its own bioluminescence, drifting off it. */
  regen: boolean;
}

export interface CreatureContext {
  depth: DepthPalette;
  state: CreatureState;
  /** Animation phase 0..1, quantized by the caller (Law L4). */
  phase: number;
  /**
   * Doc 14: the shoal noticed you. It is the LIGHT that changes, not the body —
   * a body tint is invisible at gameplay zoom, and a light stepping from `on` to
   * `hot` is the same thing a tower does when it fires.
   */
  riled?: boolean;
  /** Doc 06 Cloaked: currently untargetable, so currently wearing the shimmer. */
  hidden?: boolean;
}

/**
 * The three poses a species actually authors.
 *
 * `hurt` and `death` are not among them on purpose: both are the animal itself
 * with a transform over it — a flash, and a body coming apart — so the builder
 * draws the idle pose and applies them. Nineteen species cannot disagree about
 * what dying looks like if none of them draws it.
 */
export type PoseState = 'idle' | 'move' | 'attack';

export interface CreatureAnchor {
  /** Anchor position within the surface. Always integers. */
  ax: number;
  ay: number;
}

/** The colours a species is handed. Nobody derives their own — that is Law L5. */
export interface CreaturePaint {
  ramp: FaunaRamp;
  /** This state's step of the depth's bioluminescence. */
  glow: Color;
  /** One step under it: secondary lights, brood glow, mote trails. */
  halo: Color;
}

/** Draws the creature's own body. Outline, markers and state transforms are the builder's. */
export type CreatureDraw = (
  s: PixelSurface,
  c: CreatureAnchor,
  ctx: CreatureContext & { state: PoseState },
  p: CreaturePaint,
) => void;

/**
 * Tail throw. A resting animal still moves — doc 10's "every living thing moves
 * at rest" — but a swimming one commits, and that difference is what separates
 * `idle` from `move` across the whole bestiary without nineteen species each
 * inventing a second cycle.
 */
export function tailThrow(ctx: CreatureContext): number {
  const f = [0, 1, 0, -1][Math.min(3, Math.floor((ctx.phase - Math.floor(ctx.phase)) * 4))]!;
  return ctx.state === 'idle' ? f : f * 2;
}

/** A 1 px forward lean on the driving beats of a swim cycle. Zero at rest. */
export function bodyLean(ctx: CreatureContext): number {
  if (ctx.state === 'idle') return 0;
  const k = Math.min(3, Math.floor((ctx.phase - Math.floor(ctx.phase)) * 4));
  return k === 1 || k === 3 ? 1 : 0;
}

/**
 * Stride height for anything that walks rather than swims. The same idea as
 * `tailThrow` for the half of the bestiary that has legs: a resting crab
 * shuffles, a walking one picks its feet up.
 */
export function gait(ctx: CreatureContext): number {
  return ctx.state === 'idle' ? 1 : 2;
}

/**
 * Which states a species has, derived from its own content row.
 *
 * A Thread Node has speed 0 and dps 0 — doc 06 calls it furniture the Mother
 * leaves behind — so it has no swim cycle and no bite, and demanding a `move`
 * frame from it would be the harness inventing a requirement the game does not
 * have. An Abyssal Chorister has dps 0 for the same reason: its aura is the
 * attack, and the sim never gives it a bite target.
 */
export function statesFor(speed: number, dps: number): CreatureState[] {
  const out: CreatureState[] = ['idle'];
  if (speed > 0) out.push('move');
  if (dps > 0) out.push('attack');
  out.push('hurt', 'death');
  return out;
}

export interface CreatureSpec {
  id: string;
  /** Display name, from `src/content/enemies.json` — never retyped. */
  name: string;
  /** Doc 16 §1 splits these: bosses are hand-tuned set pieces, fauna is a class. */
  class: 'creature' | 'boss';
  size: CreatureSize;
  /** Must be unique across every creature (doc 10's silhouette contract). */
  silhouetteFamily: string;
  /** The one sentence a reviewer needs to judge whether the sprite is the animal. */
  read: string;
  markers: Markers;
  /** The states this species has — derived from its speed and dps, never authored. */
  states: CreatureState[];
  /** Depths this species can appear at — derived from content, never authored. */
  depths: string[];
  /** Stamped at Pass F, after stakeholder sign-off. */
  reviewedAt: string | null;
  draw: CreatureDraw;
}

/**
 * Which step of its own bioluminescence a creature shows. Global, exactly like
 * `signatureStep` for structures: "function is readable by light alone at any
 * zoom" (doc 10 rule 6) only holds if every animal agrees what biting looks
 * like.
 */
export function glowStep(state: CreatureState, riled = false): 'off' | 'dim' | 'on' | 'hot' {
  if (state === 'death') return 'off'; // the light going out IS the death frame
  if (state === 'attack' || state === 'hurt' || riled) return 'hot';
  return 'on';
}
